import React, { useState } from 'react';
import { Card, Button, Row, Col, Modal, Form } from 'react-bootstrap';
import OrganizationForm from '../projects/OrganizationForm';
import { formatCurrency } from '../../utils/formatters';
import axios from 'axios';

const OrgSelect = ({ organizations, fetchOrganizations, onSelectOrg }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [editLoading, setEditLoading] = useState(false);

  const handleOrgCreated = () => {
    setShowCreateModal(false);
    if (fetchOrganizations) fetchOrganizations();
  };

  const handleEditClick = (e, org) => {
    e.stopPropagation(); // Prevent selecting the organization
    setEditingOrg(org);
    setEditForm({
      name: org.name || '',
      description: org.description || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await axios.put(`/api/organizations/${editingOrg._id}`, editForm);
      alert('Organization updated successfully');
      setShowEditModal(false);
      if (fetchOrganizations) fetchOrganizations();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.msg || 'Error updating organization');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="org-select">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h4 mb-1">Select Organization</h2>
          <p className="text-muted small mb-0">Choose an organization workspace to manage settings, team permissions, and Plaid keys.</p>
        </div>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={() => setShowCreateModal(true)}
        >
          <i className="fas fa-plus mr-2"></i>New Organization
        </Button>
      </div>

      <Row>
        {organizations.length === 0 ? (
          <Col md={12} className="text-center py-5">
            <i className="fas fa-building fa-3x text-muted mb-3"></i>
            <h5>No Organizations Found</h5>
            <p className="text-muted small">Create your first organization to begin managing projects and users.</p>
          </Col>
        ) : (
          organizations.map((org) => (
            <Col key={org._id} md={4} className="mb-4">
              <Card 
                className="h-100 shadow-sm border-0 org-card" 
                style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                onClick={() => onSelectOrg(org)}
              >
                <Card.Body className="d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="font-weight-bold mb-0 text-truncate" style={{ maxWidth: '75%' }}>{org.name}</h5>
                      <div className="d-flex align-items-center">
                        <Button 
                          variant="link" 
                          className="p-0 text-muted mr-2 hover-primary"
                          onClick={(e) => handleEditClick(e, org)}
                          title="Rename / Edit Organization"
                        >
                          <i className="fas fa-edit small"></i>
                        </Button>
                        <i className="fas fa-building text-primary"></i>
                      </div>
                    </div>
                    <p className="text-muted small text-truncate-2" style={{ height: '40px' }}>
                      {org.description || 'No description provided.'}
                    </p>
                  </div>

                  <div className="mt-3 pt-3 border-top">
                    <Row className="text-center">
                      <Col xs={6} className="border-right">
                        <div className="text-muted extra-small font-weight-bold text-uppercase">Projects</div>
                        <div className="h6 mb-0 font-weight-bold text-dark">{org.projects?.length || 0}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted extra-small font-weight-bold text-uppercase">Total Cash</div>
                        <div className="h6 mb-0 font-weight-bold text-success">
                           {formatCurrency(org.summary?.totalCash || 0)}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton className="py-2 px-3 bg-light">
          <Modal.Title className="h6 mb-0">Create Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <OrganizationForm onOrgCreated={handleOrgCreated} />
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton className="py-2 px-3 bg-light">
          <Modal.Title className="h6 mb-0">Edit Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="small font-weight-bold">Organization Name</Form.Label>
              <Form.Control 
                type="text" 
                name="name" 
                value={editForm.name} 
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} 
                size="sm"
                required 
                placeholder="e.g. Real Estate Group"
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="small font-weight-bold">Description</Form.Label>
              <Form.Control 
                as="textarea" 
                name="description" 
                value={editForm.description} 
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} 
                size="sm"
                rows={3}
                placeholder="Brief description..."
              />
            </Form.Group>
            <Button type="submit" variant="primary" size="sm" block disabled={editLoading}>
              {editLoading ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .org-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
        }
        .text-truncate-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;  
          overflow: hidden;
        }
        .extra-small {
          font-size: 0.65rem;
          letter-spacing: 0.5px;
        }
        .hover-primary:hover {
          color: #007bff !important;
        }
      `}} />
    </div>
  );
};

export default OrgSelect;
